/**
 * @file きき分けあそびのピアノ音源 (Web Audio)。
 * @why: 出題はピアノの実録標本 (public/kikiwake/*.mp3、CC BY 3.0 = 同フォルダ NOTICE.txt)。
 *   和音は 2 秒くらいで弾く (R12、s.69)。基準ピッチは A=442 (R13) に合わせ、A=440 の標本を
 *   再生レート 442/440 で使う。標本が読めない環境では倍音合成のつなぎ音にフォールバック
 *   する (機能を止めない)。
 * @failure: AudioContext の生成と fetch は unlock() まで遅延する (iOS Safari の自動再生
 *   制約はユーザー操作起点の resume で解ける。jsdom などコンテキストが無い環境でも
 *   構築だけでは落ちない)。個々の標本の取得失敗はその音だけ合成にフォールバック。
 */

export interface KikiwakeAudio {
  /** ユーザー操作起点で呼ぶ (再生の解錠 + 標本の読み込み)。何度呼んでもよい。 */
  unlock(): Promise<void>;
  /** 和音 (音名の配列) を鳴らし、鳴り終わりで resolve する。 */
  playChord(notes: readonly string[]): Promise<void>;
  /** AudioContext を閉じる (画面を離れるとき。ブラウザの生成数上限とメモリのため)。 */
  dispose(): void;
}

/** 音名 (C4 など、白鍵のみ) → MIDI ノート番号。 */
function midi(note: string): number {
  const semis: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  const letter = note.slice(0, 1);
  const octave = Number(note.slice(1));
  const s = semis[letter];
  if (s === undefined || Number.isNaN(octave)) throw new Error(`unknown note: ${note}`);
  return 12 * (octave + 1) + s;
}

const GATE_SEC = 2.0; // R12: 和音は 2 秒くらい
const RELEASE_SEC = 0.25;
/** 標本の収録基準ピッチ (A=440)。原典指定の A=442 (R13) へは再生レート比で合わせる。 */
const STANDARD_PITCH_A = 440;

export function createKikiwakeAudio(pitchA = 442): KikiwakeAudio {
  let ctx: AudioContext | null = null;
  const buffers = new Map<string, AudioBuffer>();
  let loading: Promise<void> | null = null;

  const ensureCtx = (): AudioContext => {
    if (!ctx) ctx = new AudioContext();
    return ctx;
  };

  const loadNote = async (c: AudioContext, note: string): Promise<void> => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}kikiwake/${note}.mp3`);
      if (!res.ok) throw new Error(`${res.status}`);
      const buf = await c.decodeAudioData(await res.arrayBuffer());
      buffers.set(note, buf);
    } catch (err) {
      // この音だけ合成にフォールバック (playChord 側で分岐)
      console.warn(`[kikiwake] sample unavailable for ${note}`, err);
    }
  };

  const unlock = async (): Promise<void> => {
    const c = ensureCtx();
    if (c.state === "suspended") await c.resume();
    if (!loading) {
      const notes = ["A3", "B3", "C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5"];
      loading = Promise.all(notes.map((n) => loadNote(c, n))).then(() => undefined);
    }
    await loading;
  };

  const playSample = (c: AudioContext, buf: AudioBuffer, when: number, out: GainNode): void => {
    const src = c.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = pitchA / STANDARD_PITCH_A;
    src.connect(out);
    src.start(when);
    src.stop(when + GATE_SEC + RELEASE_SEC + 0.1);
  };

  const playSynth = (c: AudioContext, note: string, when: number, out: GainNode): void => {
    // ピアノ様のつなぎ音: 基音 + 2 倍音 + 3 倍音を減衰させる
    const freq = STANDARD_PITCH_A * Math.pow(2, (midi(note) - 69) / 12) * (pitchA / STANDARD_PITCH_A);
    const partials: [number, number][] = [
      [1, 0.5],
      [2, 0.18],
      [3, 0.08],
    ];
    for (const [mult, amp] of partials) {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.frequency.value = freq * mult;
      osc.type = "sine";
      g.gain.setValueAtTime(amp, when);
      g.gain.exponentialRampToValueAtTime(amp * 0.12, when + GATE_SEC);
      osc.connect(g).connect(out);
      osc.start(when);
      osc.stop(when + GATE_SEC + RELEASE_SEC);
    }
  };

  const playChord = async (notes: readonly string[]): Promise<void> => {
    await unlock();
    const c = ensureCtx();
    const when = c.currentTime + 0.05;
    const out = c.createGain();
    out.gain.setValueAtTime(1, when);
    out.gain.setValueAtTime(1, when + GATE_SEC);
    out.gain.linearRampToValueAtTime(0, when + GATE_SEC + RELEASE_SEC);
    out.connect(c.destination);
    for (const note of notes) {
      const buf = buffers.get(note);
      if (buf) playSample(c, buf, when, out);
      else playSynth(c, note, when, out);
    }
    await new Promise<void>((resolve) => {
      setTimeout(resolve, Math.ceil((GATE_SEC + 0.1) * 1000));
    });
  };

  const dispose = (): void => {
    if (ctx && ctx.state !== "closed") void ctx.close();
    ctx = null;
    buffers.clear();
    loading = null;
  };

  return { unlock, playChord, dispose };
}
